import React, { useEffect, useCallback, useMemo } from 'react';
import { NSCard, NSDropdown, NSInput } from 'bricks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronDoubleRight, faDollar, faPercentage,
} from '@fortawesome/pro-regular-svg-icons';
import { formatDecimalAsPercentage, formatUSDCurrency, numeralFormatterCurrency } from 'ns_libs/formatter';
import { calculateTotalDeveloperFee } from 'views/ProFormaWizard/helpers/calculateTotalDeveloperFeeHelper';
import { DEVELOPMENT_SOURCE_METRIC } from 'constants/sources';
import { BOE_FIELD, BOESlideoutFormValues } from '../types';
import { useBOECalculations, useNumberFormatter } from '../hooks';
import {
    BOE_PROPERTY_TYPES, COST_BASIS_OPTIONS, COST_BASIS_TYPES, HARD_SOFT_COSTS_OPTIONS, DATA_CENTER_COST_BASIS_OPTIONS,
    DATA_CENTER_HARD_SOFT_COSTS_OPTIONS, LOAN_CALCULATION_OPTIONS, UNIT_OF_TIME_OPTIONS,
} from '../constants';
import './BOECalculator.scss';

export interface BOECalculatorProps {
    assetTypeOptions?: { value: string; label: string }[];
    selectedAssetType?: string;
    onAssetTypeChange?: (option: { value: string; label: string }) => void;
    boeValues: BOESlideoutFormValues;
    setBoeValues: React.Dispatch<React.SetStateAction<BOESlideoutFormValues>>
}

export const BOECalculator: React.FC<BOECalculatorProps> = ({
    assetTypeOptions,
    selectedAssetType,
    onAssetTypeChange,
    boeValues,
    setBoeValues,
}) => {
    const handleChange = <K extends keyof BOESlideoutFormValues>(
        field: K,
        value: BOESlideoutFormValues[K],
    ) => {
        setBoeValues(prev => ({ ...prev, [field]: value }));
    };

    // calculations hook
    const {
        netRentable,
        sfPerUnit,
        grossPotentialRent,
        expenseTotal,
        netOperatingIncome,
        exitValuation,
        hardCostsTotal,
        softCostsTotal,
        developerFeeTotal,
        interestReserveTotal,
        loanAmount,
        equityCost,
        totalDevelopmentCosts,
        returnOnCost,
        returnOnEquity,
        profitReturn,
    } = useBOECalculations(boeValues);

    const { formatNumber } = useNumberFormatter();

    // update derived fields
    useEffect(() => {
        setBoeValues(prev => ({
            ...prev,
            developerFeeTotal,
            totalDevelopmentCosts,
            loanAmount,
            interestReserveTotal,
            equityCost,
        }));
    }, [developerFeeTotal, totalDevelopmentCosts, loanAmount, interestReserveTotal, equityCost]);

    // helper for currency display
    const getDisplayValue = useCallback((value: number | null | undefined, placeholder: string = 'TBD'): string => {
        if (value === 0 || Number.isNaN(value)) {
            return placeholder;
        }
        return numeralFormatterCurrency(value ?? 0);
    }, []);

    const timeUnitOptions = useMemo(() => UNIT_OF_TIME_OPTIONS.map(option => ({
        ...option,
        label: (() => {
            if (option.label === 'Monthly') return 'months';
            if (option.label === 'Annual') return 'years';
            return option.label;
        })(),
    })), []);

    const costBasisOptions = useMemo(() => {
        if (selectedAssetType === BOE_PROPERTY_TYPES.DATA_CENTER) {
            return DATA_CENTER_COST_BASIS_OPTIONS;
        }
        if (selectedAssetType === BOE_PROPERTY_TYPES.APARTMENT) {
            return COST_BASIS_OPTIONS;
        }
        return COST_BASIS_OPTIONS.filter(option => option.value === COST_BASIS_TYPES.PER_SF);
    }, [selectedAssetType]);

    const hardSoftCostOptions = useMemo(() => {
        if (selectedAssetType === BOE_PROPERTY_TYPES.DATA_CENTER) {
            return DATA_CENTER_HARD_SOFT_COSTS_OPTIONS;
        }
        if (selectedAssetType === BOE_PROPERTY_TYPES.APARTMENT) {
            return HARD_SOFT_COSTS_OPTIONS;
        }
        return HARD_SOFT_COSTS_OPTIONS.filter(option => option.value !== COST_BASIS_TYPES.PER_UNIT);
    }, [selectedAssetType]);

    // Calculate developer fee total using ProFormaWizard's formula
    const calculatedDeveloperFeeTotal = useMemo(() => {
        if (!boeValues.developerFee || boeValues.developerFee === 0) return 0;

        const percentAsDecimal = boeValues.developerFee / 100;
        return calculateTotalDeveloperFee({
            developerFeePercent: percentAsDecimal,
            acquisitionCosts: boeValues.acquisitionCosts ?? 0,
            totalHardCosts: hardCostsTotal,
            totalSoftCosts: softCostsTotal,
            interestReserve: interestReserveTotal,
        });
    }, [boeValues.developerFee, boeValues.acquisitionCosts, hardCostsTotal, softCostsTotal, interestReserveTotal]);

    return (
        <div className="BOECalculator">
            <div className="row mb-2">
                <div className="col-4">
                    <NSCard className="BOECalculator-overlay d-flex flex-row justify-content-between p-2 mb-0">
                        <span>Return on cost:</span>
                        <span className="font-weight-bold">{formatDecimalAsPercentage(returnOnCost, 2)}</span>
                    </NSCard>
                </div>
                <div className="col-4">
                    <NSCard className="BOECalculator-overlay d-flex flex-row justify-content-between p-2 mb-0">
                        <span>Return on equity:</span>
                        <span className="font-weight-bold">{formatDecimalAsPercentage(returnOnEquity, 2)}</span>
                    </NSCard>
                </div>
                <div className="col-4">
                    <NSCard className="BOECalculator-overlay d-flex flex-row justify-content-between p-2 mb-0">
                        <span>Profit return:</span>
                        <span className="font-weight-bold">{formatUSDCurrency(profitReturn)}</span>
                    </NSCard>
                </div>
            </div>
            <div className="BOECalculator-overlay">
                <hr />
            </div>
            {
                !selectedAssetType && assetTypeOptions && (
                    <NSCard className="p-3">
                        <span className="font-weight-bold text-primary">ASSET TYPE</span>
                        <NSDropdown
                            options={assetTypeOptions}
                            selectedOption={boeValues.propertyType}
                            handleSelectOption={opt => {
                                handleChange('propertyType', opt);
                                onAssetTypeChange?.(opt);
                            }}
                            customSelectedOptionText={boeValues.propertyType?.label}
                        />
                    </NSCard>
                )
            }
            <NSCard className="BOECalculator-overlay p-3">
                <span className="font-weight-bold text-primary">PROPERTY SIZE</span>
                <div className="d-flex justify-content-between">
                    <div className="flex-grow-1 mr-2">
                        <NSInput
                            customClassName="BOECalculator-input"
                            labelClassName="mb-0"
                            inputGroupClassName="BOECalculator-input-addon"
                            name={BOE_FIELD.grossSquareFootage}
                            type="number"
                            label="Built gross SF"
                            placeholder="Enter amount"
                            value={boeValues.grossSquareFootage}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(BOE_FIELD.grossSquareFootage, e.target.valueAsNumber)}
                            appendInputAddon={<span>SF</span>}
                        />
                        <div className="mt-2 text-white">
                            Net rentable SF:
                            {' '}
                            <span className="text-white">{formatNumber(netRentable)}</span>
                        </div>
                    </div>
                    <div className="flex-grow-2">
                        <NSInput
                            customClassName="BOECalculator-input"
                            inputGroupClassName="BOECalculator-input-addon"
                            labelClassName="mb-0"
                            name="efficiency"
                            type="number"
                            label="Efficiency %"
                            value={boeValues.efficiency ?? 0}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('efficiency', e.target.valueAsNumber)}
                            appendInputAddon={<FontAwesomeIcon icon={faPercentage} />}
                            inputClassName="mb-2"
                            includeRange
                        />
                    </div>
                </div>
            </NSCard>

            <div className="d-flex align-items-center mb-3">
                <NSCard className="BOECalculator-overlay p-3 flex-fill w-75 mb-0">
                    <span className="font-weight-bold text-primary d-block mb-2">INCOME</span>
                    <div className="d-flex mb-1">
                        <NSDropdown
                            containerClassName="BOECalculator-dropdown"
                            badgeColor="text-primary"
                            options={UNIT_OF_TIME_OPTIONS}
                            selectedOption={UNIT_OF_TIME_OPTIONS.find(opt => opt.value === boeValues.rentPriceTime)}
                            handleSelectOption={opt => handleChange('rentPriceTime', opt.value)}
                            isBadgeToggle
                            useBodyContainer
                        />
                        <span className="ml-2 align-self-center">Rent price</span>
                    </div>
                    <NSInput
                        customClassName="BOECalculator-input"
                        inputGroupClassName="BOECalculator-input-addon"
                        labelClassName="mb-0"
                        name="rentPrice"
                        type="number"
                        placeholder="Enter amount"
                        prependInputAddon={<FontAwesomeIcon icon={faDollar} />}
                        appendInputAddon={(
                            <NSDropdown
                                options={costBasisOptions}
                                selectedOption={costBasisOptions.find(opt => opt.value === boeValues.rentPriceSubdiv)}
                                handleSelectOption={opt => handleChange('rentPriceSubdiv', opt.value as BOESlideoutFormValues['rentPriceSubdiv'])}
                                isBadgeToggle
                                badgeColor=""
                                useBodyContainer
                            />
                        )}
                        value={boeValues.rentPrice}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('rentPrice', e.target.valueAsNumber)}
                    />
                    <div className="mt-2">
                        <NSInput
                            customClassName="BOECalculator-input"
                            inputGroupClassName="BOECalculator-input-addon"
                            inputClassName="mb-2"
                            labelClassName="mb-0"
                            name="units"
                            type="number"
                            label={(() => {
                                if (selectedAssetType === BOE_PROPERTY_TYPES.DATA_CENTER) {
                                    if (boeValues.rentPriceSubdiv === 'mw') return 'Megawatts';
                                    if (boeValues.rentPriceSubdiv === 'kw') return 'Kilowatts';
                                }
                                return boeValues.propertyType?.value === BOE_PROPERTY_TYPES.APARTMENT ? 'Units' : 'Suites';
                            })()}
                            value={boeValues.units ?? 0}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('units', e.target.valueAsNumber)}
                        />
                        <span className="text-white">
                            {(() => {
                                if (selectedAssetType === BOE_PROPERTY_TYPES.DATA_CENTER) {
                                    if (boeValues.rentPriceSubdiv === 'mw') return 'SF per MW:';
                                    if (boeValues.rentPriceSubdiv === 'kw') return 'SF per kW:';
                                }
                                return 'SF per unit:';
                            })()}
                            {' '}
                            <span className="text-white">{formatNumber(sfPerUnit)}</span>
                        </span>
                    </div>
                </NSCard>
                <FontAwesomeIcon icon={faChevronDoubleRight} className="mx-3" />
                <NSCard className="BOECalculator-overlay p-3 w-50 mb-0">
                    <div>Gross potential rent</div>
                    <div className="font-weight-bold">
                        {getDisplayValue(grossPotentialRent)}
                        {grossPotentialRent ? ' annual' : ''}
                    </div>
                </NSCard>
            </div>

            <div className="d-flex align-items-center mb-3">
                <NSCard className="BOECalculator-overlay p-3 mb-0 w-75 mb-0">
                    <span className="font-weight-bold text-primary d-block mb-2">EXPENSES</span>
                    <div className="d-flex mb-1">
                        <NSDropdown
                            containerClassName="BOECalculator-dropdown"
                            badgeColor="text-primary"
                            options={UNIT_OF_TIME_OPTIONS}
                            selectedOption={UNIT_OF_TIME_OPTIONS.find(opt => opt.value === boeValues.expenseCostTime)}
                            handleSelectOption={opt => handleChange('expenseCostTime', opt.value)}
                            isBadgeToggle
                            useBodyContainer
                        />
                        <span className="ml-2 align-self-center">Expense cost</span>
                    </div>
                    <NSInput
                        customClassName="BOECalculator-input"
                        inputGroupClassName="BOECalculator-input-addon"
                        name="expenses"
                        type="number"
                        placeholder="Enter amount"
                        prependInputAddon={<FontAwesomeIcon icon={faDollar} />}
                        appendInputAddon={(
                            <NSDropdown
                                options={costBasisOptions}
                                selectedOption={costBasisOptions.find(opt => opt.value === boeValues.expenseCostSubdiv)}
                                handleSelectOption={opt => handleChange('expenseCostSubdiv', opt.value as BOESlideoutFormValues['expenseCostSubdiv'])}
                                isBadgeToggle
                                badgeColor=""
                                useBodyContainer
                            />
                        )}
                        value={boeValues.expenses}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('expenses', e.target.valueAsNumber)}
                    />
                    <div className="mt-2 text-white">
                        Expenses total:
                        {' '}
                        <span className="text-white">{getDisplayValue(expenseTotal)}</span>
                    </div>
                </NSCard>
                <FontAwesomeIcon icon={faChevronDoubleRight} className="mx-3" />
                <NSCard className="BOECalculator-overlay p-3 w-50 mb-0">
                    <div>Net operating income</div>
                    <span className="text-white">{getDisplayValue(netOperatingIncome)}</span>
                </NSCard>
            </div>

            <div className="d-flex align-items-center mb-3">
                <NSCard className="BOECalculator-overlay p-3 flex-fill w-75  mb-0">
                    <span className="font-weight-bold text-primary d-block mb-2">EXIT VALUE</span>
                    <NSInput
                        customClassName="BOECalculator-input"
                        inputGroupClassName="BOECalculator-input-addon"
                        labelClassName="mb-0"
                        name="exitCapRate"
                        type="number"
                        placeholder="Enter amount"
                        label="Assumed exit cap rate"
                        inputClassName="mb-2"
                        appendInputAddon={<FontAwesomeIcon icon={faPercentage} />}
                        value={boeValues.exitCapRate}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('exitCapRate', e.target.valueAsNumber)}
                        includeRange
                    />
                </NSCard>
                <FontAwesomeIcon icon={faChevronDoubleRight} className="mx-3" />
                <NSCard className="BOECalculator-overlay p-3 w-50  mb-0">
                    <div>Exit valuation</div>
                    <div className="font-weight-bold">{getDisplayValue(exitValuation)}</div>
                </NSCard>
            </div>

            <div className="d-flex align-items-center mb-3">
                <NSCard className="BOECalculator-overlay p-3 flex-fill w-75 mb-0">
                    <span className="font-weight-bold text-primary d-block mb-2">DEVELOPMENT COSTS</span>
                    <NSInput
                        customClassName="BOECalculator-input"
                        inputGroupClassName="BOECalculator-input-addon"
                        labelClassName="mb-0"
                        name="acquisitionCosts"
                        type="number"
                        placeholder="Enter amount"
                        label="Acquisition costs"
                        inputClassName="mb-3"
                        prependInputAddon={<FontAwesomeIcon icon={faDollar} />}
                        value={boeValues.acquisitionCosts}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('acquisitionCosts', e.target.valueAsNumber)}
                    />
                    <NSInput
                        customClassName="BOECalculator-input"
                        inputGroupClassName="BOECalculator-input-addon"
                        labelClassName="mb-0"
                        name="hardCosts"
                        type="number"
                        placeholder="Enter amount"
                        label="Hard costs"
                        prependInputAddon={<FontAwesomeIcon icon={faDollar} />}
                        appendInputAddon={(
                            <NSDropdown
                                options={hardSoftCostOptions}
                                selectedOption={hardSoftCostOptions.find(opt => opt.value === boeValues.hardCostsSubdiv)}
                                handleSelectOption={opt => handleChange('hardCostsSubdiv', opt.value)}
                                isBadgeToggle
                                badgeColor=""
                                useBodyContainer
                            />
                        )}
                        value={boeValues.hardCosts}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('hardCosts', e.target.valueAsNumber)}
                    />
                    {boeValues.hardCostsSubdiv !== COST_BASIS_TYPES.TOTAL && (
                        <div className="mt-1 text-white mb-3">
                            Hard costs total:
                            {' '}
                            <span className="text-white">{getDisplayValue(hardCostsTotal)}</span>
                        </div>
                    )}
                    <NSInput
                        customClassName="BOECalculator-input"
                        inputGroupClassName="BOECalculator-input-addon"
                        labelClassName="mb-0"
                        name="softCosts"
                        type="number"
                        placeholder="Enter amount"
                        label="Soft costs"
                        prependInputAddon={<FontAwesomeIcon icon={faDollar} />}
                        appendInputAddon={(
                            <NSDropdown
                                options={hardSoftCostOptions}
                                selectedOption={hardSoftCostOptions.find(opt => opt.value === boeValues.softCostsSubdiv)}
                                handleSelectOption={opt => handleChange('softCostsSubdiv', opt.value)}
                                isBadgeToggle
                                badgeColor=""
                                useBodyContainer
                            />
                        )}
                        value={boeValues.softCosts}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('softCosts', e.target.valueAsNumber)}
                    />
                    {boeValues.softCostsSubdiv !== COST_BASIS_TYPES.TOTAL && (
                        <div className="mt-1 mb-3 text-white">
                            Soft costs total:
                            {' '}
                            <span className="text-white">{getDisplayValue(softCostsTotal)}</span>
                        </div>
                    )}
                    <NSInput
                        customClassName="BOECalculator-input"
                        inputGroupClassName="BOECalculator-input-addon"
                        labelClassName="mb-0"
                        name="developerFee"
                        type="number"
                        placeholder="Enter amount"
                        label="Developer fee"
                        appendInputAddon={<FontAwesomeIcon icon={faPercentage} />}
                        value={boeValues.developerFee}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('developerFee', e.target.valueAsNumber)}
                    />
                    <div className="mt-1 text-white">
                        Developer fee total:
                        {' '}
                        <span className="text-white">{getDisplayValue(calculatedDeveloperFeeTotal)}</span>
                    </div>
                    <span className="mt-2 text-white">Financing costs</span>
                    <NSCard className="BOECalculator-overlay p-2 mt-0">
                        <div className="d-flex flex-row">
                            <div className="mr-2">
                                <NSInput
                                    customClassName="BOECalculator-input"
                                    inputGroupClassName="BOECalculator-input-addon"
                                    labelClassName="mb-0"
                                    name="apr"
                                    type="number"
                                    label="APR"
                                    placeholder="Enter amount"
                                    inputClassName="mb-2"
                                    appendInputAddon={<FontAwesomeIcon icon={faPercentage} />}
                                    value={boeValues.apr}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('apr', e.target.valueAsNumber)}
                                    includeRange
                                />
                            </div>
                            <div>
                                <NSInput
                                    customClassName="BOECalculator-input"
                                    inputGroupClassName="BOECalculator-input-addon"
                                    labelClassName="mb-0"
                                    name="avgLoanBalance"
                                    type="number"
                                    label="Average loan balance"
                                    placeholder="Enter amount"
                                    inputClassName="mb-2"
                                    appendInputAddon={<FontAwesomeIcon icon={faPercentage} />}
                                    value={boeValues.avgLoanBalance ?? 0}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('avgLoanBalance', e.target.valueAsNumber)}
                                    includeRange
                                />
                            </div>
                        </div>
                        <NSInput
                            customClassName="BOECalculator-input"
                            inputGroupClassName="BOECalculator-input-addon"
                            name="loanTerm"
                            type="number"
                            label="Loan term"
                            labelClassName="mt-2 mb-0"
                            placeholder="Enter amount"
                            appendInputAddon={(
                                <NSDropdown
                                    options={timeUnitOptions}
                                    selectedOption={timeUnitOptions.find(opt => opt.value === boeValues.loanTermTime)}
                                    handleSelectOption={opt => handleChange('loanTermTime', opt.value)}
                                    isBadgeToggle
                                    badgeColor=""
                                />
                            )}
                            value={boeValues.loanTerm}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('loanTerm', e.target.valueAsNumber)}
                        />
                        <div className="mt-2 text-white">
                            Interest reserve total:
                            {' '}
                            <span className="text-white">{getDisplayValue(interestReserveTotal)}</span>
                        </div>
                    </NSCard>
                </NSCard>
                <FontAwesomeIcon icon={faChevronDoubleRight} className="mx-3 align-self-center" />
                <NSCard className="BOECalculator-overlay p-3 w-50 mb-0">
                    <div>Total development costs</div>
                    <div className="font-weight-bold">{getDisplayValue(totalDevelopmentCosts)}</div>
                </NSCard>
            </div>

            <div className="d-flex mb-3">
                <NSCard className="BOECalculator-overlay p-3 w-75">
                    <div className="d-flex justify-content-between mb-2">
                        <span className="font-weight-bold text-primary">CONSTRUCTION FINANCING</span>
                    </div>
                    <div className="d-flex">
                        <NSDropdown
                            containerClassName="BOECalculator-dropdown mb-1"
                            badgeColor="text-primary"
                            options={LOAN_CALCULATION_OPTIONS}
                            selectedOption={boeValues.selectedLoanCalculationOptions}
                            handleSelectOption={opt => handleChange('selectedLoanCalculationOptions', opt)}
                            isBadgeToggle
                            useBodyContainer
                        />
                        <div />
                    </div>
                    <NSInput
                        customClassName="BOECalculator-input"
                        inputGroupClassName="BOECalculator-input-addon"
                        name="contructionFinancing"
                        type="number"
                        placeholder="Enter amount"
                        inputClassName="mb-2"
                        appendInputAddon={<FontAwesomeIcon icon={faPercentage} />}
                        value={boeValues.contructionFinancing}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('contructionFinancing', e.target.valueAsNumber)}
                        includeRange
                    />
                    {boeValues.selectedLoanCalculationOptions?.value === DEVELOPMENT_SOURCE_METRIC.LTV && (
                        <NSInput
                            customClassName="BOECalculator-input"
                            inputGroupClassName="BOECalculator-input-addon"
                            labelClassName="mt-2"
                            name="marketCapRate"
                            type="number"
                            placeholder="Enter amount"
                            label="Market cap rate %"
                            appendInputAddon={<FontAwesomeIcon icon={faPercentage} />}
                            value={boeValues.marketCapRate}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('marketCapRate', e.target.valueAsNumber)}
                        />
                    )}
                </NSCard>
                <FontAwesomeIcon icon={faChevronDoubleRight} className="mx-3 align-self-center" />
                <div className="w-50">
                    <NSCard className="BOECalculator-overlay p-3 mb-2">
                        <div>Loan amount</div>
                        <div className="font-weight-bold">{getDisplayValue(loanAmount)}</div>
                    </NSCard>
                    <NSCard className="BOECalculator-overlay p-3">
                        <div>Required equity</div>
                        <div className="font-weight-bold">{getDisplayValue(equityCost)}</div>
                    </NSCard>
                </div>
            </div>
        </div>
    );
};
